import type { MelodyGrid, MelodyMeter, MelodyTempo } from '@tunasong/schemas'

/**
 * Grid arithmetic for the melody entity.
 *
 * One beat is one QUARTER NOTE and `bpm` counts quarter notes per minute, so the grid resolution
 * is meter-independent: switching 4/4 to 6/8 moves the bar lines, never the notes. A bar is
 * `top * 4 / bottom` beats.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §2
 */

/** Onsets are built by multiplying a slot length, so they must compare equal to their slot. */
export const GRID_TOLERANCE = 1e-6

const SLOT_BEATS: Record<MelodyGrid, number> = {
  '8': 0.5,
  '16': 0.25,
  '8t': 1 / 3,
  '16t': 1 / 6,
}

/** The length of one grid slot, in quarter-note beats. */
export const gridSlotBeats = (grid: MelodyGrid): number => SLOT_BEATS[grid]

/** The length of one quarter-note beat, in ms. */
export const beatMs = (bpm: number): number => 60000 / bpm

/** The length of one grid slot, in ms. */
export const gridSlotMs = (grid: MelodyGrid, bpm: number): number => gridSlotBeats(grid) * beatMs(bpm)

/** Snap a beat position to the nearest slot. Never negative - bar 1 beat 1 is the start of time. */
export const snapToSlot = (beats: number, grid: MelodyGrid): number => {
  const slot = gridSlotBeats(grid)
  return Math.max(0, Math.round(beats / slot) * slot)
}

/** Is this beat position on a slot line? */
export const isOnGrid = (beats: number, grid: MelodyGrid): boolean =>
  Math.abs(beats - snapToSlot(beats, grid)) <= GRID_TOLERANCE

/** The length of one bar, in quarter-note beats. */
export const barBeats = (meter: MelodyMeter): number => (meter.top * 4) / meter.bottom

/**
 * The bound on `offsetMs`. Beyond half a slot the note belongs to the next slot, and the
 * quantizer (§5, sub-project 2) puts it there.
 */
export const maxOffsetMs = (tempo: MelodyTempo, grid: MelodyGrid): number => gridSlotMs(grid, tempo.bpm) / 2
